// src/pages/organismos/instituto/InstitutoHistorico.jsx
import { useState, useEffect } from 'react';
import Museo from './Museo';
import ArchivoHistorico from './ArchivoHistorico';

const directivos = [
  { cargo: "Presidente", nombre: "Francisco Sialer García", logia: "140" },
  { cargo: "Director", nombre: "Pedro Herrera Alvariño", logia: "2" },
  { cargo: "Director", nombre: "Corolario García Milena", logia: "139" },
  { cargo: "Director", nombre: "Eugenio Artaza Llona", logia: "109" },
  { cargo: "Director", nombre: "José Cabrera Cueva", logia: "3" },
  { cargo: "Director", nombre: "Juan Bezada Chavez", logia: "79" },
  { cargo: "Director", nombre: "Germán Ortiz Aguilar", logia: "172" },
  { cargo: "Director", nombre: "Jaime Munoz Acevedo", logia: "190" },
];

const InstitutoHistorico = () => {
  // Efecto para hacer scroll al inicio al cargar la vista
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeTab, setActiveTab] = useState('presentacion');

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#C5A059] selection:text-[#050505] font-sans pt-24">
      
      {/* Hero Section */}
      <section className="relative w-full pt-32 pb-16 flex flex-col items-center justify-center overflow-hidden border-b border-white/5">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1541888086925-921eb234c900?q=80&w=2000&auto=format&fit=crop')" }} 
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/90 via-[#050505]/60 to-[#050505]"></div>

        <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
          <p className="text-[#C5A059] tracking-[0.4em] uppercase text-xs md:text-sm font-light mb-4 animate-fade-in">
            Organismos Históricos
          </p>
          <h1 className="font-serif text-4xl md:text-6xl font-normal text-white tracking-tight animate-fade-in [animation-delay:0.3s] drop-shadow-lg mb-4">
            Instituto Histórico
          </h1>
          <h2 className="font-serif text-2xl md:text-3xl italic font-light text-gray-300 animate-fade-in [animation-delay:0.5s]">
            "Jorge Basadre Grohmann"
          </h2>
        </div>
      </section>

      {/* Menú de Navegación Local (Tabs) */}
      <div className="sticky top-[88px] z-40 bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/10 shadow-2xl">
        <div className="max-w-6xl mx-auto px-4 flex justify-center overflow-x-auto hide-scrollbar">
          <div className="flex gap-2 md:gap-8 min-w-max">
            {['presentacion', 'museo', 'archivo'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-4 md:px-6 text-xs md:text-sm tracking-[0.2em] uppercase font-medium transition-all duration-300 border-b-2 ${
                  activeTab === tab 
                    ? 'border-[#C5A059] text-[#C5A059]' 
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                {tab === 'presentacion' ? 'Presentación y Directorio' : tab === 'museo' ? 'Museo Masónico' : 'Archivo Histórico'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contenido Dinámico según la Pestaña */}
      <div className="max-w-[90rem] mx-auto px-6 lg:px-12 py-16">
        
        {/* TAB 1: PRESENTACIÓN */}
        {activeTab === 'presentacion' && (
          <div className="animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
              
              {/* Directorio */}
              <div className="lg:col-span-7">
                <div className="mb-8 border-b border-white/10 pb-4 flex justify-between items-end">
                  <h3 className="font-serif text-2xl md:text-3xl text-white">Consejo Directivo</h3>
                  <div className="text-right">
                    <p className="text-gray-400 text-xs tracking-widest uppercase">Periodo 2024-2026</p>
                    <p className="text-[#C5A059] text-[10px] tracking-widest uppercase mt-1">Decreto 142-021 GLP</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {directivos.map((dir, idx) => (
                    <div key={idx} className={`p-6 border ${idx === 0 ? 'bg-gradient-to-br from-[#0a0a0a] to-[#111111] border-[#C5A059]/40 shadow-lg' : 'bg-[#0a0a0a] border-white/5'}`}>
                      <span className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase block mb-3 font-semibold">{dir.cargo}</span>
                      <p className="text-gray-500 text-[10px] tracking-widest uppercase mb-1">R.·. H.·.</p>
                      <h4 className="font-serif text-xl text-white mb-2">{dir.nombre}</h4>
                      <p className="text-gray-400 text-xs font-light tracking-wide">Logia N° {dir.logia}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Información de Contacto */}
              <div className="lg:col-span-5">
                <div className="bg-[#0a0a0a] border border-white/5 p-8 md:p-10 sticky top-40">
                  <h3 className="font-serif text-2xl text-white mb-8 border-b border-white/10 pb-4">Información y Visitas</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <span className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase block mb-1">Dirección</span>
                      <p className="text-gray-300 font-light text-sm">Av. José Gálvez Barrenechea N°599, San Isidro</p>
                    </div>
                    <div>
                      <span className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase block mb-1">Central Telefónica</span>
                      <p className="text-gray-300 font-light text-sm">618-5454 <span className="text-[#C5A059] mx-2">|</span> Anexo: 221</p>
                    </div>
                    <div>
                      <span className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase block mb-1">Email</span>
                      <a href="mailto:museo@glperu.org.pe" className="text-gray-300 hover:text-[#C5A059] transition-colors font-light text-sm">museo@glperu.org.pe</a>
                    </div>
                    
                    <div className="pt-6 border-t border-white/10">
                      <span className="text-white text-xs tracking-[0.2em] uppercase block mb-4">Horarios de Atención (Lunes a Viernes)</span>
                      <div className="pl-4 border-l-2 border-[#C5A059]/30 space-y-3">
                        <div>
                          <p className="text-gray-400 text-xs tracking-widest uppercase">Empleado GLP</p>
                          <p className="text-gray-300 font-light text-sm">15:00 a 22:00 Horas</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-xs tracking-widest uppercase">Museo y Archivo Histórico</p>
                          <p className="text-gray-300 font-light text-sm">19:00 a 22:00 Horas</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* TAB 2: MUSEO MASÓNICO */}
        {activeTab === 'museo' && <Museo />}

        {/* TAB 3: ARCHIVO HISTÓRICO */}
        {activeTab === 'archivo' && <ArchivoHistorico />}

      </div>
    </div>
  );
};

export default InstitutoHistorico;